// Comités — DATOS REALES. Comités de una cooperativa, federación o confederación, con sus
// cargos (director general, subdirector, secretaria, directores de planeación y ejecución,
// suplentes numerados) y sus subcomités / dependencias especiales. Antes usaba datos de
// ejemplo (comitesData). Crear comités y asignar cargos exige rol de administración.

import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, Typography } from '@mui/material';
import { AccountTree as AccountTreeIcon, Add as AddIcon, Download as DownloadIcon, CheckCircle as CheckCircleIcon, ExpandLess, ExpandMore, Warning as WarningIcon } from '@mui/icons-material';
import FormularioDialog from '../components/FormularioDialog';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { comitesApi, organizacionApi } from '../services/coopnetApi';
import { fecha, hoy } from '../utils/formato';
import { descargar, generarExcel, generarPdf, generarWord, nombreArchivo } from '../utils/exportarEstructura';

const TIPOS_COMITE = {
  aporte_credito: 'Aporte y Crédito',
  educacion_emprendimiento: 'Educación y Emprendimiento',
  agro_ambiental_turismo: 'Agro, Ambiental y Turismo',
  salud: 'Salud',
  infraestructura: 'Infraestructura y Vivienda',
  planeacion_formulacion: 'Planeación y Formulación (Banco de Proyectos)',
  emprendimiento_ejecucion: 'Emprendimiento, Ejecución, Evaluación y Auditoría',
  etica_ddhh: 'Ética y Derechos Humanos',
  procesos_disciplinarios: 'Procesos Disciplinarios',
};
const CARGOS = {
  director_general: 'Director General',
  subdirector: 'Subdirector',
  secretaria: 'Secretaria',
  director_planeacion: 'Director de Planeación y Formulación',
  director_ejecucion: 'Director de Ejecución, Evaluación y Auditoría',
  suplente: 'Suplente',
};
// Los 10 puestos de cada comité (Art. 62/65/79): 5 cargos principales + 5 suplentes numerados.
const PUESTOS = [
  { cargo: 'director_general', numeroSuplente: null },
  { cargo: 'subdirector', numeroSuplente: null },
  { cargo: 'secretaria', numeroSuplente: null },
  { cargo: 'director_planeacion', numeroSuplente: null },
  { cargo: 'director_ejecucion', numeroSuplente: null },
  ...[1, 2, 3, 4, 5].map((n) => ({ cargo: 'suplente', numeroSuplente: n })),
];
const nombrePuesto = (p) => `${CARGOS[p.cargo] || p.cargo}${p.numeroSuplente ? ` N.º ${p.numeroSuplente}` : ''}`;
// Colores para distinguir comités (verde) de subcomités/dependencias (azul), en pantalla y en los archivos.
const COLOR_COMITE = { fuerte: '#2E7D32', suave: '#F1F8E9' };
const COLOR_SUBCOMITE = { fuerte: '#1565C0', suave: '#E3F2FD' };
const esVigente = (c) => !c.fechaFin || c.fechaFin >= hoy();
// Los 5 puestos de cada subcomité (Art. 79 parágrafo y Art. 91). Sin suplentes.
const CARGOS_SUBCOMITE = {
  coordinador_general: 'Coordinador General',
  subcoordinador: 'Subcoordinador',
  secretaria: 'Secretaria',
  coordinador_planeacion: 'Coordinador de Planeación y Formulación',
  coordinador_ejecucion: 'Coordinador de Ejecución, Evaluación y Auditoría',
};
// Arma el modelo para exportar (PDF/Word/Excel): todos los comités de la entidad con sus
// puestos vigentes, sus subcomités y los afiliados que los ocupan.
async function armarEstructura(comites, nombreEntidad, nivelTexto) {
  const vacio = (puesto) => ({ puesto, afiliado: 'Sin asignar', documento: '', desde: '', hasta: '', estado: '' });
  const detalles = await Promise.all(comites.map(async (c) => {
    const [cargos, subcomites] = await Promise.all([comitesApi.cargos(c.id), comitesApi.subcomites(c.id).catch(() => [])]);
    const cargosSub = await Promise.all(subcomites.map((sc) => comitesApi.cargosSubcomite(sc.id).catch(() => [])));
    return { c, cargos, subcomites, cargosSub };
  }));
  const ids = [...new Set(detalles.flatMap((d) => [...d.cargos, ...d.cargosSub.flat()]).map((x) => x.afiliadoId))];
  const fichas = await Promise.all(ids.map((i) => organizacionApi.obtenerAfiliado(i).catch(() => null)));
  const afiliados = new Map(fichas.filter(Boolean).map((a) => [a.id, a]));
  const ocupado = (puesto, cargo) => {
    if (!cargo) return vacio(puesto);
    const a = afiliados.get(cargo.afiliadoId);
    return {
      puesto,
      afiliado: a ? `${a.nombre} ${a.apellido}` : '—',
      documento: a ? `${a.tipoDocumento || ''} ${a.documento || ''}`.trim() : '',
      desde: fecha(cargo.fechaInicio),
      hasta: cargo.fechaFin ? fecha(cargo.fechaFin) : '',
      estado: 'Vigente',
    };
  };
  const orden = Object.keys(TIPOS_COMITE);
  return {
    entidad: nombreEntidad,
    nivel: nivelTexto,
    generado: new Date().toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }),
    comites: [...detalles]
      .sort((x, y) => orden.indexOf(x.c.tipoComite) - orden.indexOf(y.c.tipoComite))
      .map(({ c, cargos, subcomites, cargosSub }) => ({
        nombre: TIPOS_COMITE[c.tipoComite] || c.tipoComite,
        puestos: PUESTOS.map((p) => ocupado(nombrePuesto(p), cargos.find((x) => x.cargo === p.cargo && (x.numeroSuplente || null) === p.numeroSuplente && esVigente(x)))),
        subcomites: subcomites.map((sc, i) => ({
          nombre: sc.nombre,
          tipo: TIPOS_SUBCOMITE[sc.tipo] || sc.tipo,
          puestos: Object.keys(CARGOS_SUBCOMITE).map((k) => ocupado(CARGOS_SUBCOMITE[k], cargosSub[i].find((x) => x.cargo === k && esVigente(x)))),
        })),
      })),
  };
}

const TIPOS_SUBCOMITE = { subcomite_coordinacion: 'Subcomité de coordinación', dependencia_especial: 'Dependencia especial' };
const NIVELES = { confederacion: 'Confederación', federacion: 'Federación', cooperativa: 'Cooperativa' };
const opciones = (obj) => Object.entries(obj).map(([valor, etiqueta]) => ({ valor, etiqueta }));

function ComiteDetalle({ comite, tick, onAsignar, onFinalizar, onSubcomite, onAsignarSub, onFinalizarSub }) {
  const { datos, cargando, error } = useCargar(async () => {
    const [cargos, subcomites, completitud] = await Promise.all([
      comitesApi.cargos(comite.id),
      comitesApi.subcomites(comite.id).catch(() => []),
      comitesApi.completitud(comite.id).catch(() => null),
    ]);
    const cargosSub = new Map(await Promise.all(
      subcomites.map(async (sc) => [sc.id, await comitesApi.cargosSubcomite(sc.id).catch(() => [])]),
    ));
    const ids = [...new Set([...cargos, ...[...cargosSub.values()].flat()].map((c) => c.afiliadoId))];
    const fichas = await Promise.all(ids.map((i) => organizacionApi.obtenerAfiliado(i).catch(() => null)));
    const nombres = new Map(fichas.filter(Boolean).map((a) => [a.id, `${a.nombre} ${a.apellido}`]));
    return { cargos, subcomites, cargosSub, nombres, completitud };
  }, [comite.id, tick]);

  if (cargando && !datos) return <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>;
  return (
    <Box sx={{ pt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Cargos</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onAsignar(comite)}>Asignar cargo</Button>
      </Box>
      {datos?.completitud && (
        <Alert severity={datos.completitud.completo ? 'success' : 'warning'} icon={datos.completitud.completo ? <CheckCircleIcon fontSize="inherit" /> : <WarningIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          {datos.completitud.completo
            ? 'Los 10 puestos del comité están cubiertos (Art. 62/65/79 del estatuto).'
            : `Faltan ${datos.completitud.faltantes.length} de 10 puestos: ${datos.completitud.faltantes
                .map((f) => `${CARGOS[f.cargo] || f.cargo}${f.numeroSuplente ? ` N.º ${f.numeroSuplente}` : ''}`)
                .join(', ')}.`}
        </Alert>
      )}
      <TablaDatos
        cabecera={{ bgcolor: COLOR_COMITE.fuerte, color: '#fff' }}
        filas={PUESTOS.map((p) => ({
          ...p,
          id: `${p.cargo}-${p.numeroSuplente || 0}`,
          ocupante: (datos?.cargos || []).find((c) => c.cargo === p.cargo && (c.numeroSuplente || null) === p.numeroSuplente && esVigente(c)),
        }))}
        vacio="Sin puestos."
        columnas={[
          { titulo: 'Puesto', render: (p) => nombrePuesto(p) },
          { titulo: 'Afiliado', render: (p) => (p.ocupante ? datos?.nombres.get(p.ocupante.afiliadoId) || '—' : <Chip size="small" variant="outlined" label="Sin asignar" />) },
          { titulo: 'Desde', render: (p) => (p.ocupante ? fecha(p.ocupante.fechaInicio) : '') },
          { titulo: 'Hasta', render: (p) => (p.ocupante ? (p.ocupante.fechaFin ? fecha(p.ocupante.fechaFin) : 'Sin fecha (dato anterior)') : '') },
          {
            titulo: '',
            render: (p) => (p.ocupante
              ? <Button size="small" color="warning" onClick={() => onFinalizar(p.ocupante)}>Finalizar</Button>
              : <Button size="small" onClick={() => onAsignar(comite, { cargo: p.cargo, numeroSuplente: p.numeroSuplente })}>Asignar</Button>),
          },
        ]}
      />
      {(datos?.cargos || []).some((c) => !esVigente(c)) && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1 }}>Historial (cargos vencidos o finalizados)</Typography>
          <TablaDatos
            filas={(datos?.cargos || []).filter((c) => !esVigente(c))}
            vacio=""
            columnas={[
              { titulo: 'Puesto', render: (c) => nombrePuesto({ cargo: c.cargo, numeroSuplente: c.numeroSuplente }) },
              { titulo: 'Afiliado', render: (c) => datos?.nombres.get(c.afiliadoId) || '—' },
              { titulo: 'Desde', render: (c) => fecha(c.fechaInicio) },
              { titulo: 'Hasta', render: (c) => fecha(c.fechaFin) },
            ]}
          />
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>Subcomités y dependencias</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onSubcomite(comite)}>Nuevo</Button>
      </Box>
      {(datos?.subcomites || []).length === 0 && (
        <Typography variant="body2" color="text.secondary">Sin subcomités ni dependencias especiales.</Typography>
      )}
      {(datos?.subcomites || []).map((sc) => {
        const cargosSc = datos?.cargosSub.get(sc.id) || [];
        return (
          <Box key={sc.id} sx={{ bgcolor: COLOR_SUBCOMITE.suave, borderLeft: `6px solid ${COLOR_SUBCOMITE.fuerte}`, borderRadius: 1, p: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="small" label={TIPOS_SUBCOMITE[sc.tipo] || sc.tipo} sx={{ bgcolor: COLOR_SUBCOMITE.fuerte, color: '#fff' }} />
              <Typography variant="body2" fontWeight={700} sx={{ color: COLOR_SUBCOMITE.fuerte }}>{sc.nombre}</Typography>
            </Box>
            <TablaDatos
              cabecera={{ bgcolor: COLOR_SUBCOMITE.fuerte, color: '#fff' }}
              filas={Object.keys(CARGOS_SUBCOMITE).map((cargo) => ({
                id: cargo, cargo, ocupante: cargosSc.find((c) => c.cargo === cargo && esVigente(c)),
              }))}
              vacio=""
              columnas={[
                { titulo: 'Puesto', render: (p) => CARGOS_SUBCOMITE[p.cargo] },
                { titulo: 'Afiliado', render: (p) => (p.ocupante ? datos?.nombres.get(p.ocupante.afiliadoId) || '—' : <Chip size="small" variant="outlined" label="Sin asignar" />) },
                { titulo: 'Desde', render: (p) => (p.ocupante ? fecha(p.ocupante.fechaInicio) : '') },
                { titulo: 'Hasta', render: (p) => (p.ocupante?.fechaFin ? fecha(p.ocupante.fechaFin) : '') },
                {
                  titulo: '',
                  render: (p) => (p.ocupante
                    ? <Button size="small" color="warning" onClick={() => onFinalizarSub(p.ocupante)}>Finalizar</Button>
                    : <Button size="small" onClick={() => onAsignarSub(sc, p.cargo)}>Asignar</Button>),
                },
              ]}
            />
          </Box>
        );
      })}
    </Box>
  );
}

const Comites = () => {
  const { user, contexto } = useAuth();
  const red = useRedReal();
  const nivelVista = user?.nivel;

  // Niveles que puede consultar según la vista activa.
  const nivelesPermitidos = nivelVista === 'cooperativa' || nivelVista === 'afiliado' ? ['cooperativa'] : nivelVista === 'federacion' ? ['federacion', 'cooperativa'] : ['confederacion', 'federacion', 'cooperativa'];
  const [nivel, setNivel] = useState(() => nivelesPermitidos[0]);
  const [entidadElegida, setEntidadElegida] = useState('');

  const entidades = useMemo(() => {
    if (nivel === 'confederacion') return red.confederaciones;
    if (nivel === 'federacion') return red.federaciones.filter((f) => nivelVista !== 'federacion' || f.id === contexto?.federacion?.id);
    const activas = red.cooperativas.filter((c) => c.activa !== false);
    if (nivelVista === 'cooperativa' || nivelVista === 'afiliado') return activas.filter((c) => c.id === contexto?.cooperativa?.id);
    if (nivelVista === 'federacion') return activas.filter((c) => c.federacionId === contexto?.federacion?.id);
    return activas;
  }, [nivel, red.confederaciones, red.federaciones, red.cooperativas, nivelVista, contexto]);

  const entidadId = entidades.some((e) => e.id === entidadElegida) ? entidadElegida : entidades[0]?.id || '';
  const [abierto, setAbierto] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [tick, setTick] = useState(0);
  const [ok, setOk] = useState('');
  const recargarDetalle = () => setTick((n) => n + 1);
  const [creandoEstructura, setCreandoEstructura] = useState(false);
  const [menuDescarga, setMenuDescarga] = useState(null);
  const [descargando, setDescargando] = useState(false);
  const [errorEstructura, setErrorEstructura] = useState('');

  const { datos: comites, cargando, error, recargar } = useCargar(async () => {
    if (!entidadId) return [];
    const f = nivel === 'cooperativa' ? comitesApi.listarPorCooperativa : nivel === 'federacion' ? comitesApi.listarPorFederacion : comitesApi.listarPorConfederacion;
    return f(entidadId);
  }, [nivel, entidadId], []);

  // Afiliados que pueden ocupar un cargo (los del alcance del comité).
  const { datos: pool } = useCargar(async () => {
    if (!entidadId) return [];
    if (nivel === 'cooperativa') return (await organizacionApi.listarAfiliados(entidadId)).filter((a) => a.estado === 'activo');
    const r = await organizacionApi.listarAfiliadosRed({ [nivel === 'federacion' ? 'federacionId' : 'confederacionId']: entidadId, estado: 'activo', tamano: 100 });
    return r.items || [];
  }, [nivel, entidadId], []);

  const yaTiene = new Set(comites.map((c) => c.tipoComite));
  const nombreEntidad = entidades.find((e) => e.id === entidadId)?.nombre;
  const tiposFaltantes = Object.keys(TIPOS_COMITE).filter((t) => !yaTiene.has(t));

  const descargarEstructura = async (formato) => {
    setMenuDescarga(null); setDescargando(true); setErrorEstructura('');
    try {
      const modelo = await armarEstructura(comites, nombreEntidad, NIVELES[nivel]);
      const generar = { pdf: generarPdf, docx: generarWord, xlsx: generarExcel }[formato];
      descargar(await generar(modelo), nombreArchivo(modelo, formato));
    } catch (e) {
      setErrorEstructura(e?.message || 'No se pudo generar el archivo.');
    } finally {
      setDescargando(false);
    }
  };

  const crearEstructura = async () => {
    const msg = tiposFaltantes.length > 0
      ? `Se crearán ${tiposFaltantes.length} comité(s) en ${nombreEntidad}:\n\n${tiposFaltantes.map((t) => `• ${TIPOS_COMITE[t]}`).join('\n')}\n\nCada comité queda con sus 10 puestos y con los subcomités del estatuto (Art. 69), listos para asignar afiliados. A los comités que ya existen solo se les agregan los subcomités que les falten. ¿Continuar?`
      : `${nombreEntidad} ya tiene los 9 comités. Se agregarán los subcomités del estatuto (Art. 69) que les falten. ¿Continuar?`;
    if (!window.confirm(msg)) return;
    setCreandoEstructura(true); setErrorEstructura('');
    try {
      const r = await comitesApi.crearEstructura(nivel, entidadId);
      setOk(`Estructura lista: ${r?.creados?.length ?? 0} comité(s) y ${r?.subcomitesCreados ?? 0} subcomité(s) nuevos. Asigna los afiliados en cada puesto.`);
      recargarDetalle();
      recargar();
    } catch (e) {
      setErrorEstructura(e?.message || 'No se pudo crear la estructura de comités.');
    } finally {
      setCreandoEstructura(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Comités</Typography>
          <Typography variant="body1" color="text.secondary">Comités de dirección, cargos y subcomités</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {entidadId && !cargando && (
            <Button variant="outlined" startIcon={creandoEstructura ? <CircularProgress size={16} /> : <AccountTreeIcon />} disabled={creandoEstructura} onClick={crearEstructura}>
              {tiposFaltantes.length > 0 ? 'Crear estructura de comités' : 'Completar estructura (subcomités)'}
            </Button>
          )}
          <Button variant="outlined" startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />} disabled={descargando || cargando || comites.length === 0} onClick={(e) => setMenuDescarga(e.currentTarget)}>
            Descargar estructura
          </Button>
          <Menu anchorEl={menuDescarga} open={Boolean(menuDescarga)} onClose={() => setMenuDescarga(null)}>
            <MenuItem onClick={() => descargarEstructura('pdf')}>PDF</MenuItem>
            <MenuItem onClick={() => descargarEstructura('docx')}>Word (.docx)</MenuItem>
            <MenuItem onClick={() => descargarEstructura('xlsx')}>Excel (.xlsx)</MenuItem>
          </Menu>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!entidadId} onClick={() => setDlg({ tipo: 'comite' })}>Nuevo comité</Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {nivelesPermitidos.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Nivel</InputLabel>
            <Select label="Nivel" value={nivel} onChange={(e) => { setNivel(e.target.value); setEntidadElegida(''); setAbierto(null); }}>
              {nivelesPermitidos.map((n) => <MenuItem key={n} value={n}>{NIVELES[n]}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {(entidades.length > 1 || nivelesPermitidos.length > 1) && (
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>{NIVELES[nivel]}</InputLabel>
            <Select label={NIVELES[nivel]} value={entidadId} onChange={(e) => { setEntidadElegida(e.target.value); setAbierto(null); }}>
              {entidades.map((e) => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {errorEstructura && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorEstructura('')}>{errorEstructura}</Alert>}
      {(error || red.error) && <Alert severity="error" sx={{ mb: 2 }}>{error || red.error}</Alert>}

      {red.cargando || cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !entidadId ? (
        <Alert severity="info">Aún no hay {NIVELES[nivel].toLowerCase()}s registradas en tu alcance.</Alert>
      ) : comites.length === 0 ? (
        <Alert severity="info">{nombreEntidad} aún no tiene comités. Usa «Crear estructura de comités» para crear los 9 comités del estatuto con sus puestos y subcomités, o «Nuevo comité» para crearlos uno a uno.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {comites.map((c) => (
            <Card key={c.id} variant="outlined" sx={{ bgcolor: COLOR_COMITE.suave, borderLeft: `6px solid ${COLOR_COMITE.fuerte}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip size="small" label="Comité" sx={{ bgcolor: COLOR_COMITE.fuerte, color: '#fff' }} />
                      <Typography variant="h6" sx={{ color: COLOR_COMITE.fuerte }}>{TIPOS_COMITE[c.tipoComite] || c.tipoComite}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Comité de {NIVELES[nivel].toLowerCase()} · creado el {fecha(c.creadoEn)}</Typography>
                  </Box>
                  <IconButton onClick={() => setAbierto(abierto === c.id ? null : c.id)}>{abierto === c.id ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </Box>
                <Collapse in={abierto === c.id} unmountOnExit>
                  <ComiteDetalle comite={c} tick={tick} onAsignar={(x, puesto) => setDlg({ tipo: 'cargo', comite: x, puesto })} onFinalizar={(cargo) => setDlg({ tipo: 'finalizar', cargo })} onSubcomite={(x) => setDlg({ tipo: 'subcomite', comite: x })} onAsignarSub={(sc, cargo) => setDlg({ tipo: 'cargoSub', subcomite: sc, cargo })} onFinalizarSub={(cargo) => setDlg({ tipo: 'finalizarSub', cargo })} />
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <FormularioDialog
        abierto={dlg?.tipo === 'comite'}
        titulo={`Nuevo comité — ${nombreEntidad || ''}`}
        campos={[{ campo: 'tipoComite', label: 'Tipo de comité', tipo: 'select', requerido: true, opciones: opciones(TIPOS_COMITE).filter((o) => !yaTiene.has(o.valor)) }]}
        textoBoton="Crear comité"
        onGuardar={async (v) => { await comitesApi.crear(nivel, entidadId, { tipoComite: v.tipoComite, comitePadreId: null }); setOk('Comité creado.'); recargar(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'cargo'}
        titulo={dlg?.puesto ? `Asignar: ${nombrePuesto(dlg.puesto)}` : 'Asignar cargo'}
        descripcion={dlg?.comite ? TIPOS_COMITE[dlg.comite.tipoComite] : ''}
        campos={[
          { campo: 'afiliadoId', label: 'Afiliado', tipo: 'select', requerido: true, opciones: pool.map((a) => ({ valor: a.id, etiqueta: `${a.nombre} ${a.apellido}${a.cooperativa ? ` — ${a.cooperativa}` : ''}` })) },
          ...(dlg?.puesto ? [] : [
            { campo: 'cargo', label: 'Cargo', tipo: 'select', requerido: true, opciones: opciones(CARGOS) },
            { campo: 'numeroSuplente', label: 'N.º de suplente', tipo: 'numero', ayuda: 'Solo para el cargo «Suplente» (1 a 5)' },
          ]),
          { campo: 'fechaInicio', label: 'Fecha de inicio', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fechaInicio: hoy() }}
        textoBoton="Asignar"
        onGuardar={async (v) => {
          const cargo = dlg.puesto ? dlg.puesto.cargo : v.cargo;
          const numeroSuplente = dlg.puesto ? dlg.puesto.numeroSuplente : (v.cargo === 'suplente' && Number(v.numeroSuplente) > 0 ? Number(v.numeroSuplente) : null);
          await comitesApi.asignarCargo(dlg.comite.id, { afiliadoId: v.afiliadoId, cargo, numeroSuplente, fechaInicio: v.fechaInicio });
          setOk('Cargo asignado.'); recargarDetalle();
        }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'finalizar'}
        titulo="Finalizar cargo"
        descripcion={dlg?.cargo ? CARGOS[dlg.cargo.cargo] : ''}
        campos={[{ campo: 'fechaFin', label: 'Fecha de finalización', tipo: 'fecha', requerido: true }]}
        inicial={{ fechaFin: hoy() }}
        textoBoton="Finalizar"
        onGuardar={async (v) => { await comitesApi.finalizarCargo(dlg.cargo.id, v.fechaFin); setOk('Cargo finalizado.'); recargarDetalle(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'cargoSub'}
        titulo={dlg?.cargo ? `Asignar: ${CARGOS_SUBCOMITE[dlg.cargo]}` : 'Asignar'}
        descripcion={dlg?.subcomite?.nombre || ''}
        campos={[
          { campo: 'afiliadoId', label: 'Afiliado', tipo: 'select', requerido: true, opciones: pool.map((a) => ({ valor: a.id, etiqueta: `${a.nombre} ${a.apellido}${a.cooperativa ? ` — ${a.cooperativa}` : ''}` })) },
          { campo: 'fechaInicio', label: 'Fecha de inicio', tipo: 'fecha', requerido: true },
        ]}
        inicial={{ fechaInicio: hoy() }}
        textoBoton="Asignar"
        onGuardar={async (v) => {
          await comitesApi.asignarCargoSubcomite(dlg.subcomite.id, { afiliadoId: v.afiliadoId, cargo: dlg.cargo, fechaInicio: v.fechaInicio });
          setOk('Cargo del subcomité asignado.'); recargarDetalle();
        }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'finalizarSub'}
        titulo="Finalizar cargo del subcomité"
        descripcion={dlg?.cargo?.cargo ? CARGOS_SUBCOMITE[dlg.cargo.cargo] : ''}
        campos={[{ campo: 'fechaFin', label: 'Fecha de finalización', tipo: 'fecha', requerido: true }]}
        inicial={{ fechaFin: hoy() }}
        textoBoton="Finalizar"
        onGuardar={async (v) => { await comitesApi.finalizarCargoSubcomite(dlg.cargo.id, v.fechaFin); setOk('Cargo del subcomité finalizado.'); recargarDetalle(); }}
        onClose={() => setDlg(null)}
      />
      <FormularioDialog
        abierto={dlg?.tipo === 'subcomite'}
        titulo="Nuevo subcomité o dependencia"
        campos={[
          { campo: 'tipo', label: 'Tipo', tipo: 'select', requerido: true, opciones: opciones(TIPOS_SUBCOMITE) },
          { campo: 'nombre', label: 'Nombre', tipo: 'texto', requerido: true },
        ]}
        textoBoton="Crear"
        onGuardar={async (v) => { await comitesApi.crearSubcomite(dlg.comite.id, { tipo: v.tipo, nombre: v.nombre }); setOk('Subcomité creado.'); recargarDetalle(); }}
        onClose={() => setDlg(null)}
      />
    </Box>
  );
};

export default Comites;
